// src/api/clients.js
import axios from "axios";
import { API_BASE as SBIN_API_BASE, buildAuthHeaders } from "./base";

const API = SBIN_API_BASE;
const DEFAULT_CLIENT_LIST_LIMIT = 5000;
const SEARCH_CLIENT_LIST_LIMIT = 50;

function authHeaders() {
  return {
    "Content-Type": "application/json",
  };
}

function adminAuthHeaders(token) {
  return buildAuthHeaders(token, {
    "Content-Type": "application/json",
  });
}

/**
 * List clients, optionally filtered by salon and search.
 *
 * @param {Object} params
 * @param {number=} params.salon_id
 * @param {string=} params.search
 */
export async function listClients(params = {}) {
  const nextParams = { ...params };
  if (nextParams.limit == null) {
    nextParams.limit =
      String(nextParams.search || "").trim() || String(nextParams.notes_keyword || "").trim()
        ? SEARCH_CLIENT_LIST_LIMIT
        : DEFAULT_CLIENT_LIST_LIMIT;
  }
  const res = await axios.get(`${API}/clients`, {
    params: nextParams,
    headers: authHeaders(),
  });
  return res.data;
}

export async function getClientCount(params = {}) {
  const res = await axios.get(`${API}/clients/count`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Get a single client by id.
 */
export async function getClient(clientId) {
  const res = await axios.get(`${API}/clients/${clientId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// Alias for clarity in callers
export const getClientById = getClient;

/**
 * Create a new client.
 *
 * Example payload:
 * {
 *   full_name: "Jane Client",
 *   email: "jane@example.com",
 *   phone: "555-1234",
 *   notes: "Prefers morning appointments",
 *   salon_id: 1
 * }
 */
export async function createClient(data) {
  const res = await axios.post(`${API}/clients`, data, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Update a client.
 * You can send partial data – backend supports PATCH-like behavior via PUT.
 */
export async function updateClient(clientId, data) {
  const res = await axios.put(`${API}/clients/${clientId}`, data, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function mergeClients(payload) {
  const res = await axios.post(`${API}/clients/merge`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Soft-delete a client (sets is_active = false).
 */
export async function deleteClient(clientId) {
  await axios.delete(`${API}/clients/${clientId}`, {
    headers: authHeaders(),
  });
}

export async function getDuplicateEmails(token) {
  const res = await axios.get(`${API}/admin/clients/duplicate-emails`, {
    headers: adminAuthHeaders(token),
  });
  return res.data;
}

export async function deactivateClient(clientId, token) {
  const res = await axios.patch(`${API}/admin/clients/${clientId}/deactivate`, null, {
    headers: adminAuthHeaders(token),
  });
  return res.data;
}

/**
 * Fetch the service history for a specific client.
 *
 * @param {number|string} clientId
 * @param {Object} [options]
 * @param {number} [options.limit=50]
 */
export async function getClientServiceLog(clientId, options = {}) {
  const { limit = 50 } = options;
  const res = await axios.get(`${API}/clients/${clientId}/service-log`, {
    params: { limit },
    headers: authHeaders(),
  });
  const payload = res.data;
  return Array.isArray(payload) ? payload : [];
}

export async function getClientTickets(clientId, options = {}) {
  const { salon_id, start, end } = options;
  const params = {};
  if (salon_id) params.salon_id = salon_id;
  if (start) params.start = start;
  if (end) params.end = end;
  const res = await axios.get(`${API}/clients/${clientId}/tickets`, {
    params,
    headers: authHeaders(),
  });
  const payload = res.data;
  return Array.isArray(payload) ? payload : [];
}

export async function listClientFutureAppointments(clientId) {
  const res = await axios.get(`${API}/clients/${clientId}/future-appointments`, {
    headers: authHeaders(),
  });
  const payload = res.data;
  return Array.isArray(payload) ? payload : [];
}

export async function listClientNotes(clientId, options = {}) {
  const params = {};
  if (options?.note_type) {
    params.note_type = options.note_type;
  }
  const res = await axios.get(`${API}/clients/${clientId}/notes`, {
    params,
    headers: authHeaders(),
  });
  const payload = res.data;
  if (Array.isArray(payload)) {
    return { profile_note: null, notes: payload };
  }
  if (payload && typeof payload === "object") {
    return {
      profile_note: payload.profile_note || null,
      notes: Array.isArray(payload.notes) ? payload.notes : [],
    };
  }
  return { profile_note: null, notes: [] };
}

export async function listClientEmailMessages(clientId, options = {}) {
  const { limit = 50 } = options || {};
  const res = await axios.get(`${API}/clients/${clientId}/email-messages`, {
    params: { limit },
    headers: authHeaders(),
  });
  const payload = res.data;
  return Array.isArray(payload) ? payload : [];
}

export async function listClientEmailPreferences(clientId) {
  const res = await axios.get(`${API}/clients/${clientId}/email-preferences`, {
    headers: authHeaders(),
  });
  const payload = res.data;
  return Array.isArray(payload) ? payload : [];
}

export async function updateClientEmailPreference(clientId, payload) {
  const res = await axios.put(`${API}/clients/${clientId}/email-preferences`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createClientNote(clientId, payload) {
  const res = await axios.post(`${API}/clients/${clientId}/notes`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function deleteClientNote(clientId, noteId) {
  await axios.delete(`${API}/clients/${clientId}/notes/${noteId}`, {
    headers: authHeaders(),
  });
}
